<script lang="ts">
	import { _ } from 'svelte-i18n';
	import Icon from '$lib/components/primitives/Icon.svelte';

	const steps = [
		{ icon: 'clock' as const, key: 0 },
		{ icon: 'file-text' as const, key: 1 },
		{ icon: 'credit-card' as const, key: 2 },
		{ icon: 'trending-up' as const, key: 3 }
	];
</script>

<section class="connection-flow">
	<div class="container">
		<div class="header">
			<h2 class="title">{$_('socialProof.flow.title')}</h2>
			<p class="subtitle">{$_('socialProof.flow.subtitle')}</p>
		</div>

		<div class="flow">
			{#each steps as step, i}
				<div class="flow-step" style="--i: {i}">
					<div class="step-icon">
						<Icon name={step.icon} size={28} />
					</div>
					<h3 class="step-title">{$_(`socialProof.flow.steps.${i}.title`)}</h3>
					<p class="step-desc">{$_(`socialProof.flow.steps.${i}.description`)}</p>
				</div>
				{#if i < steps.length - 1}
					<div class="flow-arrow" aria-hidden="true">
						<Icon name="arrow-right" size={18} />
					</div>
				{/if}
			{/each}
		</div>

		<p class="trust-bar">{$_('socialProof.flow.trust')}</p>
	</div>
</section>

<style>
	.connection-flow {
		padding-block: var(--section-padding-y);
		background: var(--bg-secondary);
		position: relative;
		overflow: hidden;
	}

	.connection-flow::before {
		content: '';
		position: absolute;
		inset: 0;
		background: radial-gradient(
			ellipse at 50% 0%,
			hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.06) 0%,
			transparent 60%
		);
		pointer-events: none;
	}

	.container {
		position: relative;
	}

	.header {
		text-align: center;
		max-width: 640px;
		margin-inline: auto;
		margin-bottom: var(--space-16);
	}

	.title {
		margin-bottom: var(--space-4);
		color: var(--text-primary);
		animation: fadeInUp 0.6s ease-out;
	}

	.subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: var(--line-height-relaxed);
		animation: fadeInUp 0.6s ease-out 0.1s backwards;
	}

	/* Horizontal flow of steps + arrows */
	.flow {
		display: flex;
		align-items: flex-start;
		gap: var(--space-4);
		margin-bottom: var(--space-12);
	}

	.flow-step {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-xl);
		padding: var(--space-6);
		animation: fadeInUp 0.5s ease-out calc(var(--i) * 0.1s) backwards;
		transition:
			border-color 0.2s ease,
			box-shadow 0.2s ease;
		position: relative;
	}

	.flow-step::before {
		content: counter(step);
		counter-increment: step;
		position: absolute;
		top: var(--space-4);
		right: var(--space-4);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-bold);
		color: var(--text-brand);
		opacity: 0.4;
	}

	.flow {
		counter-reset: step;
	}

	.flow-step:hover {
		border-color: hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.3);
		box-shadow: 0 4px 20px hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.08);
	}

	.step-icon {
		width: 52px;
		height: 52px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.1);
		border-radius: var(--radius-lg);
		color: var(--text-brand);
		flex-shrink: 0;
		transition: background 0.2s ease;
	}

	.flow-step:hover .step-icon {
		background: hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.15);
	}

	.step-title {
		font-size: var(--font-size-base);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		line-height: 1.3;
	}

	.step-desc {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		line-height: var(--line-height-relaxed);
	}

	.flow-arrow {
		display: flex;
		align-items: center;
		justify-content: center;
		color: var(--text-tertiary, var(--text-secondary));
		opacity: 0.4;
		flex-shrink: 0;
		margin-top: var(--space-8);
	}

	.trust-bar {
		text-align: center;
		font-size: var(--font-size-sm);
		color: var(--text-tertiary, var(--text-secondary));
		opacity: 0.7;
	}

	@keyframes fadeInUp {
		from {
			opacity: 0;
			transform: translateY(16px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	@media (max-width: 900px) {
		.flow {
			display: grid;
			grid-template-columns: 1fr 1fr;
		}

		.flow-arrow {
			display: none;
		}
	}

	@media (max-width: 560px) {
		.flow {
			grid-template-columns: 1fr;
		}
	}
</style>
